import React, { useState } from "react";

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import CardMedia from '@mui/material/CardMedia'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from "@mui/material/FormGroup";
import Chip from '@mui/material/Chip'
import CardActionArea from "@mui/material/CardActionArea"; 
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import WhatshotIcon from "@mui/icons-material/Whatshot";

// formatPrice function is now imported from context


const MenuItems = ({ menuItems, onAddToCart, translate, language, formatPrice }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [chiliLevel, setChiliLevel] = useState(1);
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  // Subcategory keywords
  const subcategoryKeywords = ["Chicken", "Beef", "Pork", "Seafood", "Liempo", "Juice", "Tea"];

  // Dynamically compute subcategories present in menuItems
  const getApplicableSubcategories = () => {
    const found = [];
    const lowerKeywords = subcategoryKeywords.map(k => k.toLowerCase());
    const applicable = new Set();
    let hasOthers = false;
    menuItems.forEach(product => {
      const name = (product.menuName || product.menuItemName || product.name || '').toLowerCase();
      let matched = false;
      lowerKeywords.forEach((keyword, i) => {
        if (name.includes(keyword)) {
          applicable.add(subcategoryKeywords[i]);
          matched = true;
        }
      });
      if (!matched) hasOthers = true;
    });
    // Always show 'All' first
    const pills = ["All", ...subcategoryKeywords.filter(k => applicable.has(k))];
    if (hasOthers) pills.push("Others");
    return pills;
  };
  const applicableSubcategories = getApplicableSubcategories();
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);

  // Keep selectedSubcategories valid if menu changes
  React.useEffect(() => {
    setSelectedSubcategories(selected => selected.filter(s => applicableSubcategories.includes(s)));
  }, [menuItems]);

  const handleSubcategoryToggle = (subcategory) => {
    if (subcategory === 'All') {
      setSelectedSubcategories([]); // All resets selection
    } else {
      setSelectedSubcategories(prev => {
        if (prev.includes(subcategory)) {
          // Remove
          return prev.filter(s => s !== subcategory);
        } else {
          // Add (remove 'All' if present)
          return [...prev.filter(s => s !== 'All'), subcategory];
        }
      });
    }
  };

  // Helper: check if pill is selected
  const isPillSelected = (subcategory) => {
    if (subcategory === 'All') return selectedSubcategories.length === 0;
    return selectedSubcategories.includes(subcategory);
  };

  // Open item details dialog
  const handleItemClick = (item) => {
    setSelectedItem(item);
    setQuantity(1);
    setChiliLevel(1);
    console.log(item);
  
    const initialOptions = {};
    if (item.options && item.options.length > 0) {
      item.options.forEach(option => {
        if (option.optionType === "single") {
          initialOptions[option.optionName] = null;
        } else if (option.optionType === "checkbox") {
          initialOptions[option.optionName] = [];
        }
      });
    }
    setSelectedOptions(initialOptions);
  };

  // Close item details dialog
  const handleCloseDialog = () => {
    setSelectedItem(null);
    setSelectedOptions({});
  };

  const handleSubcategorySelect = (subcategory) => {
    setSelectedSubcategory(subcategory);
  };

  // Filter menuItems based on selectedSubcategory
  // Helper: extract prefix (all chars before first digit)
  function extractPrefix(code) {
    if (!code || typeof code !== 'string') return '';
    code = code.trim();
    let prefix = '';
    for (let i = 0; i < code.length; i++) {
      if (/[0-9]/.test(code[i])) break;
      prefix += code[i];
    }
    return prefix;
  }
  // Helper: extract number part (first sequence of digits)
  function extractNumberPart(code) {
    if (!code || typeof code !== 'string') return '';
    code = code.trim();
    let firstDigitIdx = -1;
    for (let i = 0; i < code.length; i++) {
      if (/[0-9]/.test(code[i])) {
        firstDigitIdx = i;
        break;
      }
    }
    if (firstDigitIdx === -1) return '';
    let num = '';
    for (let i = firstDigitIdx; i < code.length; i++) {
      if (/[0-9]/.test(code[i])) num += code[i];
      else break;
    }
    return num;
  }

  // Sort items by code (prefix, then number, then fallback)
  const filteredMenuItems = React.useMemo(() => {
    let items = menuItems;
    if (selectedSubcategories.length > 0) {
      const lowerKeywords = subcategoryKeywords.map(k => k.toLowerCase());
      items = menuItems.filter(product => {
        const name = (product.menuName || product.menuItemName || product.name || '').toLowerCase();
        // If "Others" is selected, match items not matching any keyword
        const matchKeywords = selectedSubcategories.filter(s => s !== 'Others');
        let matchesAny = false;
        if (matchKeywords.length > 0) {
          matchesAny = matchKeywords.some(selected => name.includes(selected.toLowerCase()));
        }
        const isOther = selectedSubcategories.includes('Others') && !lowerKeywords.some(keyword => name.includes(keyword));
        return matchesAny || isOther;
      });
    }
    // Group by prefix
    const productsByPrefix = {};
    const productsWithoutCode = [];
    for (const item of items) {
      const code = item.code;
      if (!code || typeof code !== 'string' || !code.trim()) {
        productsWithoutCode.push(item);
        continue;
      }
      const prefix = extractPrefix(code);
      const numberPart = extractNumberPart(code);
      if (prefix) {
        if (!productsByPrefix[prefix]) productsByPrefix[prefix] = [];
        productsByPrefix[prefix].push({ item, numberPart });
      } else {
        productsWithoutCode.push(item);
      }
    }
    // Sort prefixes alphabetically
    const sortedPrefixes = Object.keys(productsByPrefix).sort();
    const sortedProducts = [];
    for (const prefix of sortedPrefixes) {
      // Sort numerically within prefix
      const group = productsByPrefix[prefix];
      group.sort((a, b) => {
        const aNum = parseInt(a.numberPart || '0', 10);
        const bNum = parseInt(b.numberPart || '0', 10);
        return aNum - bNum;
      });
      for (const { item } of group) sortedProducts.push(item);
    }
    // Add items with no/invalid code last
    sortedProducts.push(...productsWithoutCode);
    return sortedProducts;
  }, [menuItems, selectedSubcategories]);

  // Handle option selection
  const handleOptionChange = (optionName, optionType, value) => {
    if (optionType === "single") {
      setSelectedOptions({
        ...selectedOptions,
        [optionName]: value
      });
    } else if (optionType === "checkbox") {
      const currentSelections = selectedOptions[optionName] || [];

      if (currentSelections.some(item => item.name === value.name)) {
        // Remove if already selected
        setSelectedOptions({
          ...selectedOptions,
          [optionName]: currentSelections.filter(item => item.name !== value.name)
        });
      } else {
        // Add if not already selected
        setSelectedOptions({
          ...selectedOptions,
          [optionName]: [...currentSelections, value]
        });
      }
    }
  };

  // Handle adding item to cart
  const handleAddToCart = () => {
    if (!selectedItem) return;
  
    const flattenedOptions = [];
  
    Object.entries(selectedOptions).forEach(([optionName, value]) => {
      const originalOption = selectedItem.options.find(o => o.optionName === optionName);
  
      if (!originalOption) return;
  
      if (Array.isArray(value)) {
        value.forEach(choice => {
          flattenedOptions.push({
            optionId: choice._id || null,
            name: optionName,
            choice: choice.name,
            price: choice.price || 0
          });
        });
      } else if (value) {
        flattenedOptions.push({
          optionId: value._id || null,
          name: optionName,
          choice: value.name,
          price: value.price || 0
        });
      }
    });
  
    const hasSpicyTag = selectedItem.menuTags && selectedItem.menuTags.some(tag => tag.includes("spicy:"));
    const chiliLevelData = hasSpicyTag ? { chiliLevel, chiliLevelName: getChiliLevelName(chiliLevel) } : null;
  
    const finalItem = { ...selectedItem };

    if (chiliLevelData) {
      finalItem.menuName = `(${chiliLevelData.chiliLevelName}) ${finalItem.menuName}`;
    }

    onAddToCart(finalItem, flattenedOptions, quantity, chiliLevelData);
    handleCloseDialog();
  };
  
  const getChiliLevelName = (level) => {
    switch(level) {
      case 1: return "Mild";
      case 2: return "Pinoy";
      case 3: return "Thai";
      default: return "Mild";
    }
  };

  // Calculate total price including options
  const calculateTotalPrice = () => {
    if (!selectedItem) return 0;

    let total = selectedItem.basePrice;

    // Add prices from options
    Object.values(selectedOptions).forEach(value => {
      if (Array.isArray(value)) { // For checkbox options
        value.forEach(option => {
          total += option.price/100 || 0;
        });
      } else if (value) { // For single select options
        total += value.price/100 || 0;
      }
    });

    return total * quantity;
  };

  // Render allergen tags
  const renderAllergenTags = (menuTags) => {
    if (!menuTags || menuTags.length === 0) return null;

    const allergenTags = menuTags.filter(tag => tag.includes("allergen:"));

    if (allergenTags.length === 0) return null;

    return (
      <Box sx={{ mt: 1 }}>
        {allergenTags.map((tag, index) => (
          <Chip
            key={index}
            label={tag.replace("allergen:", "").trim()}
            size="small"
            color="warning"
            sx={{ mr: 0.5, mb: 0.5 }}
          />
        ))}
      </Box>
    );
  };

  // Render spicy level tags
  const renderSpicyLevelTags = (menuTags) => {
    if (!menuTags || menuTags.length === 0) return null;

    const spicyTags = menuTags.filter(tag => tag.includes("spicy:"));

    if (spicyTags.length === 0) return null;

    return (
      <Box sx={{ mt: 1 }}>
        {spicyTags.map((tag, index) => (
          <Chip
            key={index}
            label={`Spicy: ${tag.replace("spicy:", "").trim()}`}
            size="small"
            color="error"
            sx={{ mr: 0.5, mb: 0.5 }}
          />
        ))}
      </Box>
    );
  };

  const renderChiliMeter = () => {
    if (!selectedItem || !selectedItem.menuTags || !selectedItem.menuTags.some(tag => tag.includes("spicy:"))) {
      return null;
    }
  
    return (
      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>Chili Level: {getChiliLevelName(chiliLevel)}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => setChiliLevel(Math.max(1, chiliLevel - 1))}
            sx={{ border: 1, borderColor: "divider" }}
          >
            <RemoveIcon />
          </IconButton>
  
          <Box sx={{ display: 'flex', mx: 2, alignItems: 'center' }}>
            {[0, 1, 2].map((index) => (
              <WhatshotIcon 
                key={index}
                color={index < chiliLevel ? "error" : "disabled"}
                sx={{ mx: 0.5 }}
              />
            ))}
          </Box>
  
          <IconButton 
            onClick={() => setChiliLevel(Math.min(3, chiliLevel + 1))}
            sx={{ border: 1, borderColor: "divider" }}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {/* Subcategory Filter Bar (Pill Style) */}
      <Box sx={{
        display: 'flex',
        overflowX: 'auto',
        mb: 3,
        pb: 1,
        gap: { xs: 1, sm: 2 },
        px: { xs: 1, sm: 0 },
      }}>
        {applicableSubcategories.map((subcategory) => (
          <Box
            key={subcategory}
            component="button"
            onClick={() => handleSubcategoryToggle(subcategory)}
            sx={{
              border: 'none',
              outline: 'none',
              background: isPillSelected(subcategory) ? theme => theme.palette.primary.main : '#fff',
              color: isPillSelected(subcategory) ? '#fff' : theme => theme.palette.primary.main,
              fontWeight: 'bold',
              fontSize: { xs: '14px', sm: '18px' },
              borderRadius: '30px',
              border: theme => `2px solid ${theme.palette.primary.main}`,
              m: { xs: '8px 4px 8px 0', sm: '12px 8px 12px 0' },
              px: { xs: 2, sm: 4 },
              py: { xs: 0.75, sm: 1.5 },
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: isPillSelected(subcategory) ? 4 : 1,
              minWidth: { xs: '80px', sm: '120px' },
              textAlign: 'center',
              whiteSpace: 'nowrap',
              '&:hover': {
                background: theme => theme.palette.primary.light,
                color: '#fff',
              },
              touchAction: 'manipulation',
            }}
          >
            {translate ? translate(subcategory, {
              en: subcategory,
              zh: subcategory,
              th: subcategory,
              tl: subcategory
            }) : subcategory}
          </Box>
        ))}
      </Box>

      {filteredMenuItems.length === 0 ? (
        <Typography>
          {translate ? translate('No items available in this category.', {
            en: 'No items available in this category.',
            zh: '此类别中没有可用的项目。',
            th: 'ไม่มีรายการในหมวดหมู่นี้',
            tl: 'Walang available na item sa kategoryang ito.'
          }) : 'No items available in this category.'}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {filteredMenuItems.map((item) => (
            <Grid item key={item._id} xs={12} sm={6} md={4}>
              <Card
                elevation={4}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  height: { xs: 280, sm: 400 },
                  m: '12px auto',
                  borderRadius: 3,
                  overflow: 'visible',
                  boxShadow: 6,
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  background: '#fff',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 8,
                  },
                }}
              >
                <Box
                  onClick={() => handleItemClick(item)}
                  sx={{
                    cursor: 'pointer',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 0,
                    position: 'relative',
                  }}
                >
                  {/* Floating menuCode top-right */}
                  {item.code && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        bgcolor: theme => theme.palette.primary.main,
                        color: '#fff',
                        px: 2,
                        py: 0.5,
                        borderRadius: '18px',
                        fontWeight: 'bold',
                        fontSize: 16,
                        zIndex: 2,
                        boxShadow: 4,
                        letterSpacing: 1,
                        minWidth: 60,
                        textAlign: 'center',
                      }}
                    >
                      {item.code}
                    </Box>
                  )}
                  {/* Product Image */}
                  <Box
                    sx={{
                      width: '100%',
                      maxHeight: { xs: 180, sm: 300 },
                      minHeight: { xs: 120, sm: 180 },
                      mt: 1,
                      mb: 1,
                      px: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      borderRadius: 3,
                    }}
                  >
                    <img
                      src={item.menuImage || (item.images && item.images[0]) || '/assets/images/default-food.jpg'}
                      alt={item.menuName || item.menuItemName}
                      style={{
                        width: '100%',
                        maxHeight: { xs: 180, sm: 300 },
                        objectFit: 'contain',
                        borderRadius: 16,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
                        background: '#f8f8f8',
                      }}
                    />
                  </Box>
                  {/* Product Name */}
                  <Typography
                    sx={{
                      fontWeight: 'bold',
                      fontSize: { xs: 16, sm: 18 },
                      color: '#222',
                      px: 2,
                      maxHeight: 60,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {language && item.translations?.menuName?.[language] || item.menuName || item.menuItemName}
                  </Typography>
                  {/* Calories (if available) */}
                  {item.calories && (
                    <Typography
                      sx={{
                        color: '#888',
                        fontSize: { xs: 14, sm: 16 },
                        px: 2,
                        mt: 0.5,
                        maxHeight: 32,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      Calories: {item.calories} kcal
                    </Typography>
                  )}
                  {/* Allergen/Spicy tags */}
                  {/* <Box sx={{ mb: 1, mt: 1, px: 2 }}>
                    {renderAllergenTags(item.menuTags)}
                    {renderSpicyLevelTags(item.menuTags)}
                  </Box> */}
                  {/* Floating Price bottom-right */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 12,
                      right: 12,
                      bgcolor: '#fff',
                      color: theme => theme.palette.error.main,
                      px: 2.5,
                      py: 0.5,
                      borderRadius: '18px',
                      fontWeight: 'bold',
                      fontSize: { xs: 14, sm: 16 },
                      zIndex: 2,
                      // boxShadow: 4,
                      letterSpacing: 1,
                      minWidth: 80,
                      textAlign: 'center',
                      // border: theme => `2px solid ${theme.palette.error.main}`,
                    }}
                  >
                    {formatPrice(item.basePrice / 100)}
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Item Details Dialog */}
      {selectedItem && (
        <Dialog open={!!selectedItem} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {language && selectedItem.translations?.menuName?.[language] || selectedItem.menuName}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 2 }}>
              {selectedItem.menuImage && (
                <CardMedia
                  component="img"
                  image={selectedItem.menuImage}
                  alt={selectedItem.menuName}
                  sx={{ borderRadius: 1, mb: 2, width: "50%", margin: "0 auto" }}
                />
              )}

              <Typography variant="body1">
                {language && selectedItem.translations?.description?.[language] ||
                  selectedItem.description ||
                  (translate ? translate('No description available', {
                    en: 'No description available',
                    zh: '没有可用的描述',
                    th: 'ไม่มีคำอธิบาย',
                    tl: 'Walang available na paglalarawan'
                  }) : 'No description available')}
              </Typography>

              <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                {translate ? translate('Base Price', {
                  en: 'Base Price',
                  zh: '基本价格',
                  th: 'ราคาพื้นฐาน',
                  tl: 'Base Price'
                }) : 'Base Price'}: {formatPrice(selectedItem.basePrice / 100)}
              </Typography>

              {renderAllergenTags(selectedItem.menuTags)}
              {renderSpicyLevelTags(selectedItem.menuTags)}
              {renderChiliMeter()}
            </Box>

            {/* Options Section */}
            {selectedItem.options && selectedItem.options.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>Options</Typography>

                {selectedItem.options.map((option, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">{option.optionName}</FormLabel>

                      {option.optionType === "single" && (
                        <RadioGroup
                          name={option.optionName}
                          value={selectedOptions[option.optionName] ? selectedOptions[option.optionName].name : ""}
                          onChange={(e, value) => {
                            const selectedChoice = option.choices.find(c => c.name === value);
                            handleOptionChange(option.optionName, "single", selectedChoice);
                          }}
                        >
                          {option.choices.map((choice, choiceIndex) => (
                            <FormControlLabel
                              key={choiceIndex}
                              value={choice.name}
                              control={<Radio />}
                              label={
                                <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                                  <Typography>{choice.name}</Typography>
                                  {choice.price > 0 && (
                                    <Typography color="primary">
                                      + {formatPrice(choice.price / 100)}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          ))}
                        </RadioGroup>
                      )}

                      {option.optionType === "checkbox" && (
                        <FormGroup>
                          {option.choices.map((choice, choiceIndex) => (
                            <FormControlLabel
                              key={choiceIndex}
                              control={
                                <Checkbox
                                  checked={Boolean(
                                    selectedOptions[option.optionName] &&
                                    selectedOptions[option.optionName].some(item => item.name === choice.name)
                                  )}
                                  onChange={() => handleOptionChange(option.optionName, "checkbox", choice)}
                                />
                              }
                              label={
                                <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                                  <Typography>{choice.name}</Typography>
                                  {choice.price > 0 && (
                                    <Typography color="primary">
                                      + {formatPrice(choice.price / 100)}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          ))}
                        </FormGroup>
                      )}
                    </FormControl>
                  </Box>
                ))}
              </Box>
            )}

            {/* Quantity Selector */}
            <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
              <Typography variant="body1" sx={{ mr: 2 }}>Quantity:</Typography>
              <IconButton
                size="small"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                sx={{ border: 1, borderColor: "divider" }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <TextField
                value={quantity}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value) && value > 0) {
                    setQuantity(value);
                  }
                }}
                variant="outlined"
                size="small"
                inputProps={{
                  style: { textAlign: "center" },
                  min: 1
                }}
                sx={{ width: "60px", mx: 1 }}
              />
              <IconButton
                size="small"
                onClick={() => setQuantity(quantity + 1)}
                sx={{ border: 1, borderColor: "divider" }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Total Price */}
            <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
              Total: {formatPrice(calculateTotalPrice() / 100)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAddToCart}
              color="primary"
              startIcon={<AddIcon />}
            >
              Add to Cart
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default MenuItems;